/**
 * Banner Plugin
 *
 * Renders banner experiences at the top or bottom of the page.
 * Auto-shows banners when experiences are evaluated.
 */

import type { PluginFunction } from '@lytics/sdk-kit';
import type { BannerContent, Decision, Experience } from '../types';
import { sanitizeHTML } from '../utils/sanitize';

export interface BannerPluginConfig {
  banner?: {
    position?: 'top' | 'bottom';
    dismissable?: boolean;
    zIndex?: number;
    pushDown?: string; // CSS selector of element to push down (add margin-top)
  };
}

export interface BannerPlugin {
  show(experience: Experience): void;
  remove(): void;
  isShowing(): boolean;
}

/**
 * Banner Plugin
 *
 * Automatically renders banner experiences when they are evaluated.
 *
 * @example
 * ```typescript
 * import { createInstance } from '@prosdevlab/experience-sdk';
 * import { bannerPlugin } from '@prosdevlab/experience-sdk-plugins';
 *
 * // Basic usage (banner overlays at top)
 * const sdk = createInstance({
 *   banner: {
 *     position: 'top',
 *     dismissable: true
 *   }
 * });
 * sdk.use(bannerPlugin);
 *
 * // With pushDown (pushes navigation down instead of overlaying)
 * const sdk = createInstance({
 *   banner: {
 *     position: 'top',
 *     dismissable: true,
 *     pushDown: 'header' // CSS selector of element to push down
 *   }
 * });
 * sdk.use(bannerPlugin);
 * ```
 */
export const bannerPlugin: PluginFunction = (plugin, instance, config) => {
  plugin.ns('banner');

  // Set defaults
  plugin.defaults({
    banner: {
      position: 'top',
      dismissable: true,
      zIndex: 10000,
    },
  });

  // Track multiple active banners by experience ID
  const activeBanners = new Map<string, HTMLElement>();

  /**
   * Inject default banner styles if not already present
   */
  function injectDefaultStyles(): void {
    const styleId = 'xp-banner-styles';
    if (document.getElementById(styleId)) {
      return; // Already injected
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .xp-banner {
        position: fixed;
        left: 0;
        right: 0;
        width: 100%;
        font-family: var(--xp-banner-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
        font-size: var(--xp-banner-font-size, 14px);
        line-height: var(--xp-banner-line-height, 1.5);
        box-sizing: border-box;
        z-index: var(--xp-banner-z-index, 10000);
        background: var(--xp-banner-bg, #ffffff);
        color: var(--xp-banner-color, #111827);
        border-bottom: var(--xp-banner-border-width, 1px) solid var(--xp-banner-border-color, #e5e7eb);
        box-shadow: var(--xp-banner-shadow, 0 1px 3px 0 rgba(0, 0, 0, 0.05));
      }
      
      .xp-banner--top {
        top: 0;
      }
      
      .xp-banner--bottom {
        bottom: 0;
        border-bottom: none;
        border-top: var(--xp-banner-border-width, 1px) solid var(--xp-banner-border-color, #e5e7eb);
        box-shadow: var(--xp-banner-shadow-bottom, 0 -1px 3px 0 rgba(0, 0, 0, 0.05));
      }
      
      .xp-banner__container {
        display: flex;
        align-items: center;
        gap: var(--xp-banner-gap, 16px);
        max-width: var(--xp-banner-max-width, 1280px);
        margin: 0 auto;
        padding: var(--xp-banner-padding, 14px 24px);
      }
      
      .xp-banner__content {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: var(--xp-banner-content-gap, 4px);
      }
      
      .xp-banner__title {
        font-weight: var(--xp-banner-title-weight, 600);
        margin: 0;
        font-size: var(--xp-banner-title-size, 15px);
        line-height: var(--xp-banner-title-line-height, 1.4);
        color: var(--xp-banner-title-color, inherit);
      }
      
      .xp-banner__message {
        margin: 0;
        font-size: var(--xp-banner-message-size, 14px);
        line-height: var(--xp-banner-message-line-height, 1.5);
        color: var(--xp-banner-message-color, #6b7280);
      }
      
      .xp-banner__buttons {
        display: flex;
        align-items: center;
        gap: var(--xp-banner-buttons-gap, 8px);
        flex-shrink: 0;
      }
      
      .xp-banner__button {
        padding: var(--xp-banner-button-padding, 8px 16px);
        border: none;
        border-radius: var(--xp-banner-button-radius, 6px);
        font-size: var(--xp-banner-button-font-size, 14px);
        font-weight: var(--xp-banner-button-font-weight, 500);
        cursor: pointer;
        transition: all 0.2s;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
      }
      
      .xp-banner__button--primary {
        background: var(--xp-banner-button-primary-bg, #2563eb);
        color: var(--xp-banner-button-primary-color, #ffffff);
      }
      
      .xp-banner__button--primary:hover {
        background: var(--xp-banner-button-primary-bg-hover, #1d4ed8);
      }
      
      .xp-banner__button--secondary {
        background: var(--xp-banner-button-secondary-bg, #f3f4f6);
        color: var(--xp-banner-button-secondary-color, #374151);
        border: var(--xp-banner-border-width, 1px) solid var(--xp-banner-button-secondary-border, #e5e7eb);
      }
      
      .xp-banner__button--secondary:hover {
        background: var(--xp-banner-button-secondary-bg-hover, #e5e7eb);
      }
      
      .xp-banner__button--link {
        background: transparent;
        color: var(--xp-banner-button-link-color, #2563eb);
        padding: var(--xp-banner-button-link-padding, 6px 12px);
        font-weight: var(--xp-banner-button-link-font-weight, 400);
      }
      
      .xp-banner__button--link:hover {
        background: var(--xp-banner-button-link-bg-hover, #f3f4f6);
        text-decoration: underline;
      }
      
      .xp-banner__close {
        background: transparent;
        border: none;
        color: var(--xp-banner-close-color, #9ca3af);
        font-size: var(--xp-banner-close-size, 20px);
        line-height: 1;
        cursor: pointer;
        padding: var(--xp-banner-close-padding, 4px);
        margin: 0;
        transition: color 0.2s;
        flex-shrink: 0;
        width: var(--xp-banner-close-width, 28px);
        height: var(--xp-banner-close-height, 28px);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--xp-banner-close-radius, 4px);
      }
      
      .xp-banner__close:hover {
        color: var(--xp-banner-close-color-hover, #111827);
        background: var(--xp-banner-close-bg-hover, #f3f4f6);
      }
      
      @media (max-width: 640px) {
        .xp-banner__container {
          flex-wrap: wrap;
          padding: var(--xp-banner-padding-mobile, 14px 16px);
          position: relative;
        }
        
        .xp-banner__content {
          flex: 1 1 100%;
          padding-right: 32px;
        }
        
        .xp-banner__buttons {
          flex: 1 1 auto;
          width: 100%;
        }
        
        .xp-banner__button {
          flex: 1;
        }
        
        .xp-banner__close {
          position: absolute;
          top: 12px;
          right: 12px;
        }
      }
      
      /* Dark mode support - override CSS variables */
      @media (prefers-color-scheme: dark) {
        .xp-banner {
          background: var(--xp-banner-bg-dark, #111827);
          color: var(--xp-banner-color-dark, #f9fafb);
          border-bottom-color: var(--xp-banner-border-color-dark, #1f2937);
        }
        
        .xp-banner--bottom {
          border-top-color: var(--xp-banner-border-color-dark, #1f2937);
        }
        
        .xp-banner__message {
          color: var(--xp-banner-message-color-dark, #9ca3af);
        }
        
        .xp-banner__button--primary {
          background: var(--xp-banner-button-primary-bg-dark, #3b82f6);
        }
        
        .xp-banner__button--primary:hover {
          background: var(--xp-banner-button-primary-bg-hover-dark, #2563eb);
        }
        
        .xp-banner__button--secondary {
          background: var(--xp-banner-button-secondary-bg-dark, #1f2937);
          color: var(--xp-banner-button-secondary-color-dark, #f9fafb);
          border-color: var(--xp-banner-button-secondary-border-dark, #374151);
        }
        
        .xp-banner__button--secondary:hover {
          background: var(--xp-banner-button-secondary-bg-hover-dark, #374151);
        }
        
        .xp-banner__button--link {
          color: var(--xp-banner-button-link-color-dark, #60a5fa);
        }
        
        .xp-banner__button--link:hover {
          background: var(--xp-banner-button-link-bg-hover-dark, #1f2937);
        }
        
        .xp-banner__close {
          color: var(--xp-banner-close-color-dark, #6b7280);
        }
        
        .xp-banner__close:hover {
          color: var(--xp-banner-close-color-hover-dark, #f9fafb);
          background: var(--xp-banner-close-bg-hover-dark, #1f2937);
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Create banner DOM element
   */
  function createBannerElement(experience: Experience): HTMLElement {
    const content = experience.content as BannerContent;
    // Allow per-experience position override, fall back to global config
    const position = content.position ?? config.get('banner.position') ?? 'top';
    const dismissable = content.dismissable ?? config.get('banner.dismissable') ?? true;
    const zIndex = config.get('banner.zIndex') ?? 10000;

    // Inject default styles if needed
    injectDefaultStyles();

    // Create banner container
    const banner = document.createElement('div');
    banner.setAttribute('data-experience-id', experience.id);

    // Build className: base classes + position + user's custom class
    const baseClasses = ['xp-banner', `xp-banner--${position}`];
    if (content.className) {
      baseClasses.push(content.className);
    }
    banner.className = baseClasses.join(' ');

    // Apply user's custom styles
    if (content.style) {
      Object.assign(banner.style, content.style);
    }

    // Override z-index if configured
    if (zIndex !== 10000) {
      banner.style.zIndex = String(zIndex);
    }

    // Create container
    const container = document.createElement('div');
    container.className = 'xp-banner__container';
    banner.appendChild(container);

    // Create content container
    const contentDiv = document.createElement('div');
    contentDiv.className = 'xp-banner__content';

    // Add title if present
    if (content.title) {
      const title = document.createElement('h3');
      title.className = 'xp-banner__title';
      // Sanitize HTML to prevent XSS attacks
      title.innerHTML = sanitizeHTML(content.title);
      contentDiv.appendChild(title);
    }

    // Add message
    const message = document.createElement('p');
    message.className = 'xp-banner__message';
    // Sanitize HTML to prevent XSS attacks
    message.innerHTML = sanitizeHTML(content.message);
    contentDiv.appendChild(message);

    container.appendChild(contentDiv);

    // Create buttons container
    const buttonsDiv = document.createElement('div');
    buttonsDiv.className = 'xp-banner__buttons';

    // Helper function to create button with variant styling
    function createButton(buttonConfig: {
      text: string;
      action?: string;
      url?: string;
      variant?: 'primary' | 'secondary' | 'link';
      metadata?: Record<string, unknown>;
      className?: string;
      style?: Record<string, string>;
    }): HTMLButtonElement {
      const button = document.createElement('button');
      button.textContent = buttonConfig.text;

      const variant = buttonConfig.variant || 'primary';

      // Build className: base class + variant + user's custom class
      const buttonClasses = ['xp-banner__button', `xp-banner__button--${variant}`];
      if (buttonConfig.className) {
        buttonClasses.push(buttonConfig.className);
      }
      button.className = buttonClasses.join(' ');

      // Apply user's custom styles
      if (buttonConfig.style) {
        Object.assign(button.style, buttonConfig.style);
      }

      button.addEventListener('click', () => {
        // Emit action event
        instance.emit('experiences:action', {
          experienceId: experience.id,
          type: 'banner',
          action: buttonConfig.action,
          url: buttonConfig.url,
          metadata: buttonConfig.metadata,
          variant: variant,
          timestamp: Date.now(),
        });

        // Navigate if URL provided
        if (buttonConfig.url) {
          window.location.href = buttonConfig.url;
        }
      });

      return button;
    }

    // Add buttons from buttons array
    if (content.buttons && content.buttons.length > 0) {
      content.buttons.forEach((buttonConfig) => {
        const button = createButton(buttonConfig);
        buttonsDiv.appendChild(button);
      });
    }

    // Add dismiss button if dismissable
    if (dismissable) {
      const closeButton = document.createElement('button');
      closeButton.className = 'xp-banner__close';
      closeButton.innerHTML = '&times;';
      closeButton.setAttribute('aria-label', 'Close banner');

      closeButton.addEventListener('click', () => {
        remove(experience.id);
        instance.emit('experiences:dismissed', {
          experienceId: experience.id,
          type: 'banner',
        });
      });

      buttonsDiv.appendChild(closeButton);
    }

    container.appendChild(buttonsDiv);

    return banner;
  }

  /**
   * Apply pushDown margin to target element
   */
  function applyPushDown(banner: HTMLElement, position: 'top' | 'bottom'): void {
    const pushDownSelector = config.get('banner.pushDown');

    if (!pushDownSelector || position !== 'top') {
      return; // Only push down for top banners
    }

    const targetElement = document.querySelector(pushDownSelector);
    if (!targetElement || !(targetElement instanceof HTMLElement)) {
      return;
    }

    // Get banner height
    const height = banner.offsetHeight;

    // Apply margin-top with transition
    targetElement.style.transition = 'margin-top 0.3s ease';
    targetElement.style.marginTop = `${height}px`;
  }

  /**
   * Remove pushDown margin from target element
   */
  function removePushDown(): void {
    const pushDownSelector = config.get('banner.pushDown');

    if (!pushDownSelector) {
      return;
    }

    const targetElement = document.querySelector(pushDownSelector);
    if (!targetElement || !(targetElement instanceof HTMLElement)) {
      return;
    }

    // Remove margin-top with transition
    targetElement.style.transition = 'margin-top 0.3s ease';
    targetElement.style.marginTop = '0';
  }

  /**
   * Show a banner experience
   */
  function show(experience: Experience): void {
    // If banner already showing for this experience, skip
    if (activeBanners.has(experience.id)) {
      return;
    }

    // Only show if we're in a browser environment
    if (typeof document === 'undefined') {
      return;
    }

    const banner = createBannerElement(experience);
    document.body.appendChild(banner);
    activeBanners.set(experience.id, banner);

    // Apply pushDown to target element if configured
    const content = experience.content as BannerContent;
    const position = content.position ?? config.get('banner.position') ?? 'top';
    applyPushDown(banner, position);

    instance.emit('experiences:shown', {
      experienceId: experience.id,
      type: 'banner',
      timestamp: Date.now(),
    });
  }

  /**
   * Remove a banner by experience ID (or all if no ID provided)
   */
  function remove(experienceId?: string): void {
    if (experienceId) {
      // Remove specific banner
      const banner = activeBanners.get(experienceId);
      if (banner?.parentNode) {
        banner.parentNode.removeChild(banner);
      }
      activeBanners.delete(experienceId);

      // Remove pushDown if no more banners
      if (activeBanners.size === 0) {
        removePushDown();
      }
    } else {
      // Remove all banners
      for (const [id, banner] of activeBanners.entries()) {
        if (banner?.parentNode) {
          banner.parentNode.removeChild(banner);
        }
        activeBanners.delete(id);
      }

      // Remove pushDown
      removePushDown();
    }
  }

  /**
   * Check if any banner is currently showing
   */
  function isShowing(): boolean {
    return activeBanners.size > 0;
  }

  // Expose banner API
  plugin.expose({
    banner: {
      show,
      remove,
      isShowing,
    },
  });

  // Auto-show banner on experiences:evaluated event
  instance.on('experiences:evaluated', (payload: unknown) => {
    // Handle both single decision and array of decisions
    // evaluate() emits: { decision, experience }
    // evaluateAll() emits: [{ decision, experience }, ...]
    const items = Array.isArray(payload) ? payload : [payload];

    for (const item of items) {
      // Item is { decision, experience }
      const typedItem = item as { decision?: Decision; experience?: Experience };
      const decision = typedItem.decision;
      const experience = typedItem.experience;

      // Only handle banner-type experiences
      if (experience?.type === 'banner') {
        if (decision?.show) {
          show(experience);
        } else if (experience.id && activeBanners.has(experience.id)) {
          // Hide specific banner if decision says don't show
          remove(experience.id);
        }
      }
    }
  });

  // Cleanup on destroy
  instance.on('sdk:destroy', () => {
    remove();
  });
};
